import React, {useState, useEffect, useMemo} from 'react';
import {
    Button,
    Table,
    Space,
    Input,
    Form,
    Typography,
    Modal,
    message,
    Popconfirm,
    Layout,
    Row,
    Col,
    Card,
    Dropdown, MenuProps
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PrinterOutlined,
    DownOutlined
} from '@ant-design/icons';
import {
    ProductCreateItem,
    ProductDeleteItem,
    ProductSearchRequest,
    ProductSearchResponse,
    ProductUpdateItem
} from "../../model";
import {productService} from "../../services/domain/product/service";
import {useAuth} from "../../services";
import {useLocation} from "react-router-dom";
import {getEditableRoles, getPrintableRoles} from "../../utils/rolePermissions";
import ProductPdfViewer from "./components/ProductPdfViewer";
import { getPageSizeOptions, resetPaginationByTotal } from 'utils/pageUtil';

const { Title } = Typography;
const { Content } = Layout;

const ProductPage: React.FC = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<ProductSearchResponse[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentProduct, setCurrentProduct] = useState<ProductSearchResponse | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const {hasEditPermission, hasPrintPermission} = useAuth();
    const location = useLocation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [currentReportType, setCurrentReportType] = useState<string>("");
    const [reportTitle, setReportTitle] = useState<string>("");
    const [pdfViewerVisible, setPdfViewerVisible] = useState<boolean>(false);
    const [previewPdfLanguageModalVisible, setPreviewPdfLanguageModalVisible] = useState(false);
    const [printLanguage, setPrintLanguage] = useState("ZH_TW");

    const items = [
        {key: 'Products', label: '實際產品列表', icon: (<PrinterOutlined/>)},
        {key: 'ProductMembers', label: '產品會員列表', icon: (<PrinterOutlined/>)},
        {key: 'ProductMembersNametags', label: '產品會員名條', icon: (<PrinterOutlined/>)},
    ];


    // 處理範圍輸入框的變更，使起始值成為迄值的預設值
    const handleStartValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        form.setFieldsValue({
            productCodeStart: value,
            productCodeEnd: value
        });
    };

    // 搜尋產品
    const handleSearch = async (values: any) => {
        setLoading(true);
        try {
            // 構建搜尋請求 - 確保所有欄位為空時傳送空字串而非 undefined
            const searchRequest: ProductSearchRequest = {
                productCodeStart: values.productCodeStart || '',
                productCodeEnd: values.productCodeEnd || '',
                productName: values.productName || '',
                productNameEn: values.productNameEn || ''
            };

            // 調用API搜尋產品
            const result = await productService.search(searchRequest);

            if (result) {
                setProducts(result);
                setTotalRecords(result.length);
                // 重置到第一頁
                setPagination(resetPaginationByTotal(result.length));

                // 如果沒有返回數據，顯示通知
                if (result.length === 0) {
                    message.info('查無資料');
                }
            } else {
                // 如果返回的結果為null（可能是由於網絡錯誤或其他問題）
                setProducts([]);
                setTotalRecords(0);
                message.error('查詢失敗，請稍後重試');
            }
        } catch (error) {
            console.error('搜尋產品失敗:', error);
            message.error('搜尋失敗，請稍後重試');
            setProducts([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // 處理分頁、篩選和排序變更
    const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
        const newPagination = {
          current: paginationConfig.current,
          pageSize: paginationConfig.pageSize,
        };
        
        setPagination(newPagination);
    };

    // 重置表單
    const handleReset = () => {
        form.resetFields();
    };

    // 打開新增產品對話框
    const showAddModal = () => {
        setCurrentProduct(null);
        editForm.resetFields();
        setIsModalVisible(true);
    };

    // 打開編輯產品對話框
    const showEditModal = (product: ProductSearchResponse) => {
        setCurrentProduct(product);
        editForm.setFieldsValue({
            productCode: product.productCode,
            productName: product.productName,
            productNameEn: product.productNameEn || ''
        });
        setIsModalVisible(true);
    };

    // 關閉對話框
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    // 保存產品（新增或編輯）
    const handleSave = async () => {
        try {
            const values = await editForm.validateFields();
            setLoading(true);

            if (currentProduct) {
                // 編輯現有產品
                const updateItem: ProductUpdateItem = {
                    productId: currentProduct.productId,
                    productCode: values.productCode,
                    productName: values.productName,
                    productNameEn: values.productNameEn,
                    modifiedBy: 'currentUser' // 應從用戶會話中獲取
                };

                const updateResult = await productService.updateProducts({
                    products: [updateItem]
                });

                if (updateResult && updateResult.updatedCount > 0) {
                    message.success('產品更新成功');
                    setIsModalVisible(false);
                    // 刷新產品列表
                    handleSearch(form.getFieldsValue());
                } else {
                    message.error('產品更新失敗');
                }
            } else {
                // 創建新產品
                const createItem: ProductCreateItem = {
                    productCode: values.productCode,
                    productName: values.productName,
                    productNameEn: values.productNameEn,
                    createdBy: 'currentUser' // 應從用戶會話中獲取
                };

                const createResult = await productService.createProducts({
                    products: [createItem]
                });

                if (createResult && createResult.createdCount > 0) {
                    message.success('新增產品成功');
                    setIsModalVisible(false);
                    // 刷新產品列表
                    handleSearch(form.getFieldsValue());
                } else {
                    message.error('新增產品失敗');
                }
            }
        } catch (error) {
            console.error('保存產品失敗:', error);
            message.error('操作失敗，請稍後重試');
        } finally {
            setLoading(false);
        }
    };

    // 刪除產品
    const handleDelete = async (productId: number) => {
        setLoading(true);
        try {
            const deleteItem: ProductDeleteItem = {productId};
            const deleteResult = await productService.deleteProducts({
                productIds: [deleteItem]
            });

            if (deleteResult && deleteResult.deletedCount > 0) {
                message.success('產品刪除成功');
                // 刷新產品列表
                handleSearch(form.getFieldsValue());
            } else {
                message.error('產品刪除失敗');
            }
        } catch (error) {
            console.error('刪除產品失敗:', error);
            message.error('刪除失敗，請稍後重試');
        } finally {
            setLoading(false);
        }
    };

    // 處理預覽報表
    const handlePreviewReport = (reportType: string, title: string) => {
        // 檢查是否有選擇會員
        if (selectedRowKeys.length === 0) {
            message.warning('請至少選擇一個會員');
            return;
        }

        // 設置報表類型和標題
        setCurrentReportType(reportType);
        setReportTitle(title);

        // 顯示PDF預覽器
        setPdfViewerVisible(true);
    };

    const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
        const selectedItem = items.find(item => item.key === key);
        if (!selectedItem) return ;

        if(key === 'ProductMembers') {
            // 設置報表類型和標題
            setCurrentReportType(key);
            setReportTitle(selectedItem.label);
            setPreviewPdfLanguageModalVisible(true);
            return;
        }

        handlePreviewReport(key, selectedItem.label);
    };

    // 關閉PDF預覽器
    const handleClosePdfViewer = () => {
        console.log('handleClosePdfViewer')
        setPdfViewerVisible(false);
    };

    // 初始加載
    useEffect(() => {
        // 初始搜尋所有產品
        // handleSearch({});
    }, []);

    const apiParam = useMemo(() => {
        return {
            productIds: selectedRowKeys.map(key => ({productId: key.toString()})),
            language: printLanguage
        };
    }, [selectedRowKeys, printLanguage]);

    // 表格列定義
    const columns = [
        {
            title: '序號',
            dataIndex: 'index',
            key: 'index',
            render: (_: any, __: any, index: number) => index + 1
        },
        {
            title: '產品代號',
            dataIndex: 'productCode',
            key: 'productCode'
        },
        {
            title: '產品名稱',
            dataIndex: 'productName',
            key: 'productName'
        },
        {
            title: '產品英文名稱',
            dataIndex: 'productNameEn',
            key: 'productNameEn'
        },
        // 只有當使用者有編輯權限時才顯示操作欄位
        ...hasEditPermission(getEditableRoles(location.pathname)) ? [
            {
                title: '操作',
                key: 'action',
                render: (_: any, record: ProductSearchResponse) => (
                    <Space size="middle">
                        <Button
                            icon={<EditOutlined/>}
                            onClick={() => showEditModal(record)}
                            title="編輯"
                        />
                        <Popconfirm
                            title="確定要刪除此產品嗎？"
                            okText="確定"
                            cancelText="取消"
                            onConfirm={() => handleDelete(record.productId)}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined/>}
                                title="刪除"
                            />
                        </Popconfirm>
                    </Space>
                )
            }
        ] : []
    ];

    return (
        <Layout className="membership-page">
            <Content>
                {/* 頁面標題 */}
                <Row justify="center" className="mb-lg">
                    <Col>
                        <Title level={3} className="page-title">
                            <span className="page-title-icon">📋</span> 實際產品主檔
                        </Title>
                    </Col>
                </Row>

                {/* 搜尋表單 */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined />
                            <span>搜尋條件</span>
                        </Space>
                    }
                    className="card-content-spacing"
                >
                    <Form
                        form={form}
                        name="productSearch"
                        onFinish={handleSearch}
                        layout="horizontal"
                        labelCol={{xs: {span: 8}, md: {span: 6}}}
                        wrapperCol={{xs: {span: 16}, md: {span: 18}}}
                    >
                        <Row gutter={[24, 0]}>
                            {/* 產品代號範圍 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="產品代號">
                                    <Space style={{ width: '100%' }}>
                                        <Form.Item name="productCodeStart" noStyle>
                                            <Input placeholder="起始值" style={{ width: '100%' }} onChange={handleStartValueChange} />
                                        </Form.Item>
                                        <span>～</span>
                                        <Form.Item name="productCodeEnd" noStyle>
                                            <Input placeholder="結束值" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Space>
                                </Form.Item>
                            </Col>

                            {/* 產品名稱 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="產品名稱" name="productName">
                                    <Input placeholder="請輸入產品名稱" />
                                </Form.Item>
                            </Col>

                        </Row>
                        <Row gutter={[24, 0]}>
                            {/* 公司名稱 */}
                            <Col xs={24} md={12}>
                                <Form.Item label="產品英文名稱" name="productNameEn">
                                    <Input
                                        placeholder="輸入產品英文名稱"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[24, 0]} justify="center">
                            {/* 按鈕區域 */}
                            <Col xs={24}>
                                <Form.Item wrapperCol={{ span: 24 }}>
                                    <div className="form-button-container">
                                        <Button 
                                            onClick={handleReset}
                                            icon={<DeleteOutlined />}
                                        >
                                            清除
                                        </Button>
                                        <Button 
                                            type="primary" 
                                            htmlType="submit" 
                                            icon={<SearchOutlined />}
                                        >
                                            查詢
                                        </Button>
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* 搜尋結果區域 */}
                <Card
                    title={
                        <>
                            <Content>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Title level={5}>搜尋結果: {totalRecords} 筆記錄</Title>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={showAddModal}
                                                disabled={!hasEditPermission(getEditableRoles(location.pathname))}
                                            >
                                                新增產品
                                            </Button>
                                            <Dropdown
                                                menu={{
                                                    items,
                                                    onClick: handleMenuClick,
                                                }}
                                                disabled={selectedRowKeys.length === 0}
                                                trigger={['click']}
                                            >
                                                <Button icon={<DownOutlined/>} disabled={selectedRowKeys.length === 0 || !hasPrintPermission(getPrintableRoles(location.pathname))}>
                                                    列印選項
                                                </Button>
                                            </Dropdown>
                                        </Space>
                                    </Col>
                                </Row>
                            </Content>
                        </>
                    }
                >
                    <Table
                        virtual
                        scroll={{ y: 600 }}
                        rowKey="productId"
                        dataSource={products}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            ...pagination,
                            total: totalRecords,
                            showSizeChanger: true,
                            pageSizeOptions: getPageSizeOptions(totalRecords),
                            showTotal: (total) => `共 ${total} 筆`
                        }}
                        onChange={handleTableChange}
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys,
                            onChange: (selectedKeys) => {
                                setSelectedRowKeys(selectedKeys);
                            }
                        }}
                    />
                </Card>

                {/* 新增/編輯產品對話框 */}
                <Modal
                    title={currentProduct ? '編輯產品' : '新增產品'}
                    open={isModalVisible}
                    onOk={handleSave}
                    onCancel={handleCancel}
                    confirmLoading={loading}
                    okText="保存"
                    cancelText="取消"
                >
                    <Form
                        form={editForm}
                        layout="vertical"
                        name="productForm"
                    >
                        <Form.Item
                            name="productCode"
                            label="產品代號"
                            rules={[{required: true, message: '請輸入產品代號'}]}
                        >
                            <Input placeholder="請輸入產品代號"/>
                        </Form.Item>
                        <Form.Item
                            name="productName"
                            label="產品名稱"
                            rules={[{required: true, message: '請輸入產品名稱'}]}
                        >
                            <Input placeholder="請輸入產品名稱"/>
                        </Form.Item>
                        <Form.Item
                            name="productNameEn"
                            label="產品英文名稱"
                        >
                            <Input placeholder="請輸入產品英文名稱"/>
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="請選擇列印中文或英文"
                    open={previewPdfLanguageModalVisible}
                    onCancel={() => {
                        setPrintLanguage("EN_US");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onCancel')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    onOk={() => {
                        setPrintLanguage("ZH_TW");
                        setPreviewPdfLanguageModalVisible(false)
                        console.log('onOk')
                        // 顯示PDF預覽器
                        setPdfViewerVisible(true);
                    }}
                    okText="中文"
                    cancelText="英文"
                >
                </Modal>
            </Content>


            {/* PDF預覽器組件 */}
            {
                pdfViewerVisible && (
                    <ProductPdfViewer
                        visible={pdfViewerVisible}
                        onClose={handleClosePdfViewer}
                        reportType={currentReportType}
                        apiParam={apiParam}
                        title={reportTitle}
                    />
                )
            }
        </Layout>
    );
};

export default ProductPage;